import React, { useCallback, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { sendEmail, supplierFeedbackShareEmail } from '../../lib/email';
import Modal from '../Modal';
import { feedbackAnswerLabel } from '../../lib/briefFeedback';

// Share post-event feedback with the supplier — a DELIBERATE, human action
// (brief §5). The internal answers show for reference only; nothing is
// forwarded. The message is written fresh in the box and is all the supplier
// receives. Emails the supplier's active portal users and records the share on
// the feedback row.

const Spinner = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" className="animate-spin">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" fill="none" strokeOpacity="0.25" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
  </svg>
);

const SHORT = { delivered: 'Delivered', on_time: 'On time', safety: 'Safety', use_again: 'Use again' };

export default function BriefFeedbackShareModal({ brief, supplierId, supplierName, feedback, currentUser, onSuccess, onClose }) {
  const [message, setMessage] = useState(feedback?.shared_message || '');
  const [state, setState] = useState('input'); // input | submitting | error
  const [errorMessage, setErrorMessage] = useState(null);
  const submitting = state === 'submitting';
  const canSend = message.trim().length > 0;

  const submit = useCallback(async () => {
    if (!canSend || !brief?.id || !supplierId || submitting) return;
    setState('submitting'); setErrorMessage(null);
    try {
      const { data: users } = await supabase
        .from('supplier_users')
        .select('full_name, email, is_active')
        .eq('supplier_id', supplierId);
      const recipients = (users || []).filter(u => u.is_active && u.email);
      if (recipients.length === 0) {
        setErrorMessage('This supplier has no active portal user to send to.');
        setState('error');
        return;
      }
      const portalUrl = `${window.location.origin}/portal`;
      const sentTo = [];
      for (const u of recipients) {
        const { subject, html } = supplierFeedbackShareEmail({
          recipientName: u.full_name, supplierName, briefName: brief.brief_name, message: message.trim(), portalUrl,
        });
        try { await sendEmail({ to: u.email, subject, html }); sentTo.push(u.email); } catch { /* continue */ }
      }
      const nowIso = new Date().toISOString();
      await supabase.from('brief_feedback')
        .update({ shared_message: message.trim(), shared_with_supplier_at: nowIso, updated_at: nowIso })
        .eq('brief_id', brief.id).eq('supplier_id', supplierId);
      if (sentTo.length > 0) {
        await supabase.from('supplier_email_log').insert([{
          supplier_id: supplierId, email_kind: 'feedback_shared', recipients: sentTo, sent_by: currentUser?.id || null,
        }]);
      }
      onSuccess?.();
    } catch (e) {
      setErrorMessage(e.message || 'Could not send. Try again.');
      setState('error');
    }
  }, [canSend, brief?.id, brief?.brief_name, supplierId, supplierName, message, submitting, currentUser?.id, onSuccess]);

  const safeClose = () => { if (!submitting) onClose?.(); };

  return (
    <Modal open onClose={safeClose} maxWidth={520} stripe={false} ariaLabel={`Share feedback — ${supplierName || 'supplier'}`}>
      <Modal.Header eyebrow="Share feedback" title={supplierName || 'Supplier'} sub={brief?.brief_name} onClose={submitting ? undefined : safeClose} />
      <Modal.Body>
        {/* Internal answers — reference only, not sent. */}
        <div className="rounded-lg bg-[#F7F9EF] px-4 py-3 mb-4">
          <div className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.11em] text-[#51534A] mb-2">What was recorded · internal</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
            {['delivered', 'on_time', 'safety', 'use_again'].map(k => (
              <div key={k} className="flex items-baseline justify-between gap-2 font-['Metropolis'] text-[12px]">
                <span className="text-[#51534A]">{SHORT[k]}</span>
                <span className="font-semibold text-ink">{feedback?.[k] ? feedbackAnswerLabel(k, feedback[k]) : '—'}</span>
              </div>
            ))}
          </div>
          {feedback?.note && (
            <div className="mt-2 pt-2 border-t border-[#E9ECDC] font-['Metropolis'] text-[12px] text-ink-secondary whitespace-pre-wrap">{feedback.note}</div>
          )}
        </div>

        <label htmlFor="share-message" className="block font-['Metropolis'] text-[13px] font-semibold text-ink mb-2">What you'll send them</label>
        <textarea
          id="share-message"
          value={message}
          onChange={e => setMessage(e.target.value)}
          disabled={submitting}
          rows={5}
          placeholder="Write this fresh — they only see what's in this box, not your internal answers or note."
          className="w-full bg-white border border-line rounded-md px-3 py-2.5 font-['Metropolis'] text-[13px] text-ink placeholder:text-[#9BA08C] focus:outline-none focus:border-[#2F6E14] disabled:opacity-50 resize-y" />
        <p className="font-['Metropolis'] text-[11px] text-[#51534A] mt-2">Goes to the supplier's portal users. Your internal answers and note aren't included.</p>

        {state === 'error' && errorMessage && (
          <div role="alert" className="mt-4 rounded-md px-3 py-2.5 font-['Metropolis'] text-[12px]" style={{ backgroundColor: 'rgba(224,41,10,0.08)', border: '1px solid rgba(224,41,10,0.35)', color: '#E0290A' }}>
            {errorMessage}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={safeClose} disabled={submitting} className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-[#51534A] hover:text-fusion-mid-grey bg-transparent border-0 cursor-pointer disabled:opacity-50">Cancel</button>
          <button type="button" onClick={submit} disabled={!canSend || submitting}
            className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-white bg-[#2F6E14] border border-[#2F6E14] rounded-full px-5 py-2 inline-flex items-center gap-2 transition-[filter,opacity] duration-150 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed">
            {submitting ? (<><Spinner /><span>Sending…</span></>) : <span>Send to {supplierName || 'supplier'}</span>}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
